/** Labels for automation enums and catalogue keys (English source strings; translate with t()). */
import type { LucideIcon } from 'lucide-react';
import { Bell, ClipboardCheck, GitBranch, Hourglass, ShieldCheck, Sparkles, Timer, Webhook, Zap } from 'lucide-react';
import { msg, t } from '@/lib/i18n';
import type { Tone } from '@/ui';
import type { Op, StepType, TargetScope } from './model/definition';

export const RUN_STATUS: Record<string, { label: string; tone: Tone }> = {
  running: { label: msg('Running'), tone: 'info' },
  waiting: { label: msg('Waiting'), tone: 'caution' },
  done: { label: msg('Done'), tone: 'good' },
  failed: { label: msg('Failed'), tone: 'bad' },
  cancelled: { label: msg('Cancelled'), tone: 'neutral' },
};

export const STEP_STATUS: Record<string, { label: string; tone: Tone }> = {
  running: { label: msg('Running'), tone: 'info' },
  waiting: { label: msg('Waiting'), tone: 'caution' },
  done: { label: msg('Done'), tone: 'good' },
  failed: { label: msg('Failed'), tone: 'bad' },
  skipped: { label: msg('Skipped'), tone: 'neutral' },
  pending: { label: msg('Not reached'), tone: 'neutral' },
  later: { label: msg('Still to come'), tone: 'neutral' },
};

export const TASK_STATUS: Record<string, { label: string; tone: Tone }> = {
  open: { label: msg('To do'), tone: 'info' },
  done: { label: msg('Done'), tone: 'good' },
  dismissed: { label: msg('Withdrawn'), tone: 'neutral' },
  expired: { label: msg('Expired'), tone: 'caution' },
};

export const OUTCOME: Record<string, { label: string; tone: Tone }> = {
  done: { label: msg('Done'), tone: 'good' },
  approved: { label: msg('Approved'), tone: 'good' },
  rejected: { label: msg('Rejected'), tone: 'bad' },
  cancelled: { label: msg('Withdrawn'), tone: 'neutral' },
};

export const STEP_TYPE: Record<StepType, { label: string; description: string; icon: LucideIcon }> = {
  action: { label: msg('Do something'), description: msg('Run a platform action: request video, notify people, open a coaching case…'), icon: Zap },
  wait: {
    label: msg('Wait for something'),
    description: msg('Wait until an event happens to the same vehicle or driver, for example the footage arriving.'),
    icon: Hourglass,
  },
  delay: { label: msg('Pause'), description: msg('Wait a fixed time before going on.'), icon: Timer },
  approval: {
    label: msg('Ask for approval'),
    description: msg('A person must approve before the automation goes on; a rejection stops it.'),
    icon: ShieldCheck,
  },
  task: { label: msg('Create a task'), description: msg('Give a person something to do; the automation goes on when it is done.'), icon: ClipboardCheck },
  ai: { label: msg('Ask the assistant'), description: msg('Let the assistant write a text or decide yes or no, for a later step to use.'), icon: Sparkles },
  branch: { label: msg('Choose a path'), description: msg('Go to another step depending on a condition.'), icon: GitBranch },
};

/**
 * Steps as the "Add a step" menu offers them: notifying comes first (dispatching is "anything that
 * notifies something or someone"); Notify and Send to a system are action steps with their action.
 */
export interface StepChoice {
  key: string;
  type: StepType;
  action?: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

export const NOTIFY_ACTION = 'comms.notify';
export const WEBHOOK_ACTION = 'integrations.send_webhook';

export const STEP_CHOICES: StepChoice[] = [
  {
    key: 'notify',
    type: 'action',
    action: NOTIFY_ACTION,
    label: msg('Notify someone'),
    description: msg('A message to people in a role, the driver, a colleague or an email address.'),
    icon: Bell,
  },
  {
    key: 'webhook',
    type: 'action',
    action: WEBHOOK_ACTION,
    label: msg('Send to a system'),
    description: msg('Post the event to another system (a webhook).'),
    icon: Webhook,
  },
  { key: 'task', type: 'task', ...STEP_TYPE.task },
  { key: 'approval', type: 'approval', ...STEP_TYPE.approval },
  { key: 'action', type: 'action', label: msg('Do something else'), description: STEP_TYPE.action.description, icon: Zap },
  { key: 'wait', type: 'wait', ...STEP_TYPE.wait },
  { key: 'delay', type: 'delay', ...STEP_TYPE.delay },
  { key: 'branch', type: 'branch', ...STEP_TYPE.branch },
  { key: 'ai', type: 'ai', ...STEP_TYPE.ai },
];

/** How a step presents itself (a Notify step is an action step with comms.notify). */
export function stepKind(type: StepType, action?: string | null): { label: string; icon: LucideIcon } {
  if (type === 'action' && action === NOTIFY_ACTION) return { label: msg('Notify'), icon: Bell };
  if (type === 'action' && action === WEBHOOK_ACTION) return { label: msg('Send to a system'), icon: Webhook };
  return STEP_TYPE[type];
}

/** Operators as words, for the condition builder and the "reads as" sentence. */
export const OP_LABEL: Record<Op, string> = {
  '=': msg('is'),
  '!=': msg('is not'),
  '>': msg('is more than'),
  '>=': msg('is at least'),
  '<': msg('is less than'),
  '<=': msg('is at most'),
  in: msg('is one of'),
  not_in: msg('is none of'),
  contains: msg('contains'),
  exists: msg('is known'),
};

export const SCOPE_LABEL: Record<TargetScope, { label: string; description: string }> = {
  all: { label: msg('Everything'), description: msg('Every vehicle, driver and asset the trigger is about.') },
  groups: { label: msg('Groups'), description: msg('Members of the chosen groups (sub-groups included).') },
  vehicles: { label: msg('Specific vehicles'), description: msg('Only the vehicles you pick.') },
  drivers: { label: msg('Specific drivers'), description: msg('Only the drivers you pick.') },
  assets: { label: msg('Specific assets'), description: msg('Only the assets you pick.') },
};

export const SUBJECT: Record<string, string> = {
  vehicle: msg('vehicle'),
  driver: msg('driver'),
  asset: msg('asset'),
  device: msg('device'),
  place: msg('place'),
  project: msg('project'),
  coaching_case: msg('coaching case'),
  any: msg('subject'),
  none: msg('subject'),
};

/** What starts a flow, phrased to follow "When …". Unknown triggers use their catalogue label. */
export const TRIGGER_PHRASE: Record<string, string> = {
  'telemetry.detection': msg('an event is detected'),
  'places.visit.started': msg('a vehicle arrives at a place'),
  'places.visit.ended': msg('a vehicle leaves a place'),
  'places.asset.condition': msg('an asset rule fires'),
  'places.asset.condition_cleared': msg('an asset rule clears'),
  'media.video.available': msg('video footage arrives'),
  'media.request.failed': msg('a video request fails or expires'),
  'coaching.case.opened': msg('a coaching case is opened'),
  'coaching.session.confirmed': msg('a coaching session is confirmed'),
  'coaching.session.held': msg('a coaching session is held'),
  'coaching.session.no_show': msg('a driver misses a coaching session'),
  'coaching.followup.due': msg('a coaching follow-up is due'),
  'comms.reply.received': msg('a driver or user replies'),
  'tacho.infringement.detected': msg('an EU 561 infringement is detected'),
  'tacho.download.failed': msg('a tachograph download fails'),
  'tacho.download.overdue': msg('a tachograph download is overdue'),
  'tacho.card.expiring': msg('a company card is about to expire'),
  'insurance.claim.created': msg('a claim is opened'),
  'insurance.claim.status_changed': msg('a claim changes status'),
  'quality.finding.opened': msg('a data-quality problem is found'),
  'quality.installation.failed': msg('an installation check fails'),
  'admin.access_changed': msg('someone’s access changes'),
  'admin.setting_changed': msg('a setting is changed'),
};

/** Driving events (telemetry detection types) as nouns. */
export const DETECTION: Record<string, string> = {
  harsh_accel: msg('harsh acceleration'),
  harsh_brake: msg('harsh brake'),
  harsh_corner: msg('harsh cornering'),
  speeding: msg('speeding'),
  tailgating: msg('tailgating'),
  fcw: msg('forward collision warning'),
  pcw: msg('pedestrian collision warning'),
  ldw: msg('lane departure warning'),
  distraction: msg('distraction'),
  phone: msg('phone use'),
  yawning: msg('yawning'),
  eyes_closed: msg('eyes closed'),
  smoking: msg('smoking'),
  fatigue: msg('fatigue'),
  camera_blocked: msg('blocked camera'),
  idling: msg('long idling'),
  fuel_drop: msg('fuel drop'),
};

/** The unit of a detection's value, by type. */
export const DETECTION_UNIT: Record<string, string> = {
  harsh_accel: 'g',
  harsh_brake: 'g',
  harsh_corner: 'g',
  idling: 's',
  fuel_drop: 'L',
};

/** Enum values that read better as words. Anything else is shown with spaces for underscores. */
export const OPTION: Record<string, string> = {
  low: msg('low'),
  medium: msg('medium'),
  high: msg('high'),
  info: msg('info'),
  warning: msg('warning'),
  critical: msg('critical'),
  forward: msg('forward'),
  inward: msg('cabin'),
  inapp: msg('in the app'),
  email: msg('email'),
  sms: msg('SMS'),
  whatsapp: msg('WhatsApp'),
  push: msg('push notification'),
  mon: msg('Monday'),
  tue: msg('Tuesday'),
  wed: msg('Wednesday'),
  thu: msg('Thursday'),
  fri: msg('Friday'),
  sat: msg('Saturday'),
  sun: msg('Sunday'),
  user: msg('a person'),
  automation: msg('an automation'),
  vehicle: msg('vehicle'),
  asset: msg('asset'),
  manual: msg('by hand'),
  flow: msg('an automation'),
  planner: msg('the planner'),
  agent: msg('the assistant'),
  threshold: msg('threshold'),
  geofence: msg('geofence'),
  time_window: msg('time window'),
  connectivity: msg('connectivity'),
  driver_card: msg('driver card'),
  vehicle_unit: msg('vehicle unit'),
  failed: msg('failed'),
  timed_out: msg('timed out'),
};

/** Payload fields that are not in a trigger's declared fields but every payload may carry. */
export const COMMON_FIELDS: Record<string, string> = {
  vehicle_id: msg('Vehicle'),
  driver_id: msg('Driver'),
  asset_id: msg('Asset'),
  project_id: msg('Project'),
  detection_id: msg('Event'),
  subject_id: msg('Subject'),
  subject_type: msg('Subject type'),
  severity: msg('Severity'),
  title: msg('Title'),
  ts: msg('Date and time'),
};

/** Grouping of triggers and actions in pickers, by the module that declares them. */
export const MODULE: Record<string, string> = {
  telemetry: msg('Driving events'),
  trips: msg('Trips'),
  media: msg('Video'),
  places: msg('Places and assets'),
  coaching: msg('Coaching'),
  comms: msg('Messages'),
  tacho: msg('Tachograph'),
  insurance: msg('Insurance'),
  quality: msg('Data quality'),
  admin: msg('Administration'),
  flows: msg('Automation'),
  automation: msg('Automation'),
  tasks: msg('Tasks'),
  fleet: msg('Fleet'),
  reports: msg('Reports'),
  insights: msg('Insights'),
};

export const moduleLabel = (key: string | null | undefined): string => (key ? t(MODULE[key] ?? key) : t('Automation'));

export const optionLabel = (v: unknown): string => {
  const s = String(v);
  return OPTION[s] ? t(OPTION[s]) : DETECTION[s] ? t(DETECTION[s]) : s.replace(/_/g, ' ');
};

export const detectionLabel = (v: unknown): string => (DETECTION[String(v)] ? t(DETECTION[String(v)] as string) : String(v).replace(/_/g, ' '));

export const statusOf = (map: Record<string, { label: string; tone: Tone }>, key: string | null | undefined): { label: string; tone: Tone } => {
  const hit = key ? map[key] : undefined;
  return hit ? { label: t(hit.label), tone: hit.tone } : { label: key ?? '—', tone: 'neutral' };
};
